import { decimal } from '@snap-types/schemas/decimal';
import z from 'zod';

import { AlphaQuoteEvaluationStatus } from '../enums';
import { base } from './base';

export const strictInputs = {
  quantity: decimal,

  sems1Cost: decimal.nullish(),
  sems2Cost: decimal.nullish(),
  osp2Price: decimal.nullish(),
  osp3Price: decimal.nullish(),

  /**
   * Manual overrides. The user's value survives switching an override off, so
   * these stay populated even while the matching flag is false.
   *
   * Gross margin and unit price are two ways of saying the same thing once a
   * unit cost is known, so at most one of the two is ever overridden.
   *
   * The flags default to false to match the column default: a producer with no
   * concept of overrides — a spreadsheet import, the embed — simply omits them.
   */
  userGrossMargin: decimal.nullish(),
  grossMarginIsOverridden: z.boolean().default(false),

  userUnitPrice: decimal.nullish(),
  unitPriceIsOverridden: z.boolean().default(false),

  userLeadTimeWeeks: z.number().int().nullish(),
  leadTimeWeeksIsOverridden: z.boolean().default(false),
};

export const alphaQuotePriceStrictInputsSchema = z.object(strictInputs);

export type AlphaQuotePriceStrictInputs = z.output<
  typeof alphaQuotePriceStrictInputsSchema
>;

export const strictCalculatedData = {
  totalPreTrimWeight: decimal,
  totalFinalWeight: decimal,

  headingRunHours: decimal,
  headingLaborReduction: decimal,
  headingLaborCost: decimal,
  headingBurdenReduction: decimal,
  headingBurdenCost: decimal,
  headingCost: decimal,

  pinchRunHours: decimal.nullish(),
  pinchLaborReduction: decimal.nullish(),
  pinchLaborCost: decimal.nullish(),
  pinchBurdenReduction: decimal.nullish(),
  pinchBurdenCost: decimal.nullish(),
  pinchCost: decimal.nullish(),

  rollingRunHours: decimal.nullish(),
  rollingLaborReduction: decimal.nullish(),
  rollingLaborCost: decimal.nullish(),
  rollingBurdenReduction: decimal.nullish(),
  rollingBurdenCost: decimal.nullish(),
  rollingCost: decimal.nullish(),

  trimmingRunHours: decimal.nullish(),
  trimmingLaborReduction: decimal.nullish(),
  trimmingLaborCost: decimal.nullish(),
  trimmingBurdenReduction: decimal.nullish(),
  trimmingBurdenCost: decimal.nullish(),
  trimmingCost: decimal.nullish(),

  shankSlottingRunHours: decimal.nullish(),
  shankSlottingLaborReduction: decimal.nullish(),
  shankSlottingLaborCost: decimal.nullish(),
  shankSlottingBurdenReduction: decimal.nullish(),
  shankSlottingBurdenCost: decimal.nullish(),
  shankSlottingCost: decimal.nullish(),

  sawSlottingRunHours: decimal.nullish(),
  sawSlottingLaborReduction: decimal.nullish(),
  sawSlottingLaborCost: decimal.nullish(),
  sawSlottingBurdenReduction: decimal.nullish(),
  sawSlottingBurdenCost: decimal.nullish(),
  sawSlottingCost: decimal.nullish(),

  dephosCost: decimal.nullish(),
  heatTreatCost: decimal.nullish(),
  materialCost: decimal,
  sems1TotalCost: decimal,
  sems2TotalCost: decimal,

  serrationsCost: decimal.nullish(),
  surfaceFinishCost: decimal,
  postFinishCost: decimal.nullish(),
  osp2Cost: decimal.nullish(),
  osp3Cost: decimal.nullish(),
  toolingCost: decimal,

  // --- Pricing ---
  totalCost: decimal,

  unitCost: decimal,
  revenue: decimal,
  finalGrossMargin: decimal,

  /**
   * Resolved values: the override where one is set, otherwise what the system
   * arrived at. The `calculated*` twin is always what the system arrived at,
   * so an overridden break can still show what it would have been.
   */
  grossMargin: decimal,
  calculatedGrossMargin: decimal,

  unitPrice: decimal,
  calculatedUnitPrice: decimal,

  // --- Lead Time ---
  leadTimeWeeks: z.number().int(),
  calculatedLeadTimeWeeks: z.number().int(),
};

export const alphaQuotePriceStrictCalculatedDataSchema =
  z.object(strictCalculatedData);

export type AlphaQuotePriceStrictCalculatedData = z.output<
  typeof alphaQuotePriceStrictCalculatedDataSchema
>;

export const strict = {
  ...base,
  ...strictInputs,
  ...strictCalculatedData,
  evaluationStatus: z.enum([AlphaQuoteEvaluationStatus.CALCULATED]),
};

export const alphaQuotePriceStrictSchema = z.object(strict);

export type AlphaQuotePriceStrict = z.output<
  typeof alphaQuotePriceStrictSchema
>;
